import {
	columnFilteringFeature,
	columnGroupingFeature,
	columnOrderingFeature,
	columnPinningFeature,
	columnResizingFeature,
	columnSizingFeature,
	columnVisibilityFeature,
	createExpandedRowModel,
	createFilteredRowModel,
	createGroupedRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	filterFn_arrIncludes,
	filterFn_equals,
	filterFn_inDateRange,
	filterFn_inNumberRange,
	filterFn_includesString,
	filterFn_weakEquals,
	globalFilteringFeature,
	metaHelper,
	rowExpandingFeature,
	rowPaginationFeature,
	rowSelectionFeature,
	rowSortingFeature,
	sortFn_alphanumeric,
	sortFn_datetime,
	sortFn_text,
	tableFeatures,
} from "@tanstack/react-table";
import type { ColumnMeta } from "./types";

/**
 * Everything the DataTable stitches into a table instance: the features it renders, the row
 * models those features process, the function registries they resolve names from, and the
 * type of `columnDef.meta`. TanStack Table registers optional features explicitly — one that
 * is not listed here has no state, no options and no methods, on the instance and in its
 * types.
 *
 * The registries hold exactly the names the auto-resolvers look up. `getAutoSortFn` samples a
 * column's first rows and asks for `datetime`, `alphanumeric` or `text`; `getAutoFilterFn`
 * asks for `includesString`, `inNumberRange`, `equals`, `arrIncludes`, `inDateRange` or
 * `weakEquals` by the type of the first value; the search box is `includesString`. A name
 * missing here degrades silently in production — basic sorting, or no filter at all — and
 * only warns in development.
 */
export const dataTableFeatures = tableFeatures({
	columnFilteringFeature,
	columnGroupingFeature,
	columnOrderingFeature,
	columnPinningFeature,
	columnResizingFeature,
	columnSizingFeature,
	columnVisibilityFeature,
	globalFilteringFeature,
	rowExpandingFeature,
	rowPaginationFeature,
	rowSelectionFeature,
	rowSortingFeature,
	expandedRowModel: createExpandedRowModel(),
	filteredRowModel: createFilteredRowModel(),
	groupedRowModel: createGroupedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	sortedRowModel: createSortedRowModel(),
	filterFns: {
		arrIncludes: filterFn_arrIncludes,
		equals: filterFn_equals,
		inDateRange: filterFn_inDateRange,
		inNumberRange: filterFn_inNumberRange,
		includesString: filterFn_includesString,
		weakEquals: filterFn_weakEquals,
	},
	sortFns: {
		alphanumeric: sortFn_alphanumeric,
		datetime: sortFn_datetime,
		text: sortFn_text,
	},
	columnMeta: metaHelper<ColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;
